import * as React from 'react'
import { Box, Tooltip } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import { ScatterChart } from '@mui/x-charts/ScatterChart'
import { toPng } from 'html-to-image'

import { Icon, IconButton, Typography } from '~components/internal'
import { chartColors, chartDimensions } from '~tokens/internal'
import { type AssistantChartData, isPointSeries } from '~utils/chartUtils'
import { slugify } from '~utils/textUtils'

import './AssistantChartRenderer.scss'

export type ChartSize = 'small' | 'medium'

interface Props {
  chart: AssistantChartData
  size?: ChartSize
}

const SIZE_CONFIG = {
  medium: {
    pieHeight: chartDimensions.height.lg,
    pieOuterRadius: chartDimensions.pie.outerRadius.lg,
    categoryAxis: {},
  },
  small: {
    pieHeight: chartDimensions.height.sm,
    pieOuterRadius: chartDimensions.pie.outerRadius.sm,
    categoryAxis: {
      tickLabelStyle: { fontSize: 10 },
      tickLabelInterval: (): boolean => true,
    },
  },
} as const

async function downloadChartImage(
  node: HTMLElement,
  filename: string,
  backgroundColor: string,
): Promise<void> {
  const dataUrl = await toPng(node, {
    backgroundColor,
    cacheBust: true,
    width: node.offsetWidth,
    height: node.scrollHeight,
  })

  const img = new Image()
  await new Promise<void>((resolve, reject) => {
    img.onload = (): void => resolve()
    img.onerror = (): void => reject(new Error('Failed to load chart image'))
    img.src = dataUrl
  })

  const scale = img.width / node.offsetWidth
  const padding = Math.round(24 * scale)

  const canvas = document.createElement('canvas')
  canvas.width = img.width + padding * 2
  canvas.height = img.height + padding * 2
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, padding, padding)

  const link = document.createElement('a')
  link.href = canvas.toDataURL('image/png')
  link.download = filename
  link.click()
}

const AssistantChartRenderer: React.FC<Props> = ({ chart, size = 'medium' }) => {
  const { chartType, title, xAxis, yAxis, series = [], data = [] } = chart
  const exportRef = React.useRef<HTMLDivElement>(null)
  const singleSeries = series.length <= 1
  const axisHeight = chartDimensions.height.lg
  const { pieHeight, pieOuterRadius, categoryAxis } = SIZE_CONFIG[size]

  const handleDownload = async (): Promise<void> => {
    const node = exportRef.current
    if (!node) return
    const filename = `${slugify(title ?? chartType, 'chart')}.png`
    await downloadChartImage(node, filename, '#ffffff')
  }

  const renderChart = (): React.ReactElement | null => {
    const dataset = (xAxis?.data ?? []).map((label, i) => {
      const row: Record<string, string | number> = { category: label }
      series.forEach((s, si) => {
        if (!isPointSeries(s.data)) row[`s${si}`] = s.data[i] ?? 0
      })
      return row
    })
    const multiSeriesConfig = series.map((s, i) => ({
      dataKey: `s${i}`,
      label: s.label,
      color: s.color ?? chartColors[i % chartColors.length],
    }))

    const allSeriesValues = series.flatMap((s) =>
      (s.data as (number | { x: number; y: number })[]).map((point) =>
        typeof point === 'number' ? point : point.y,
      ),
    )
    const allIntegers =
      allSeriesValues.length > 0 && allSeriesValues.every((value) => Number.isInteger(value))
    const integerYTicks = yAxis?.allowDecimals === undefined ? allIntegers : !yAxis.allowDecimals
    const yAxisConfig = { min: 0, ...(integerYTicks ? { tickMinStep: 1 } : {}) }

    switch (chartType) {
      case 'bar':
        return (
          <BarChart
            height={axisHeight}
            dataset={dataset}
            xAxis={[
              {
                scaleType: 'band',
                dataKey: 'category',
                ...categoryAxis,
                ...(singleSeries
                  ? { colorMap: { type: 'ordinal' as const, colors: [...chartColors] } }
                  : {}),
              },
            ]}
            yAxis={[yAxisConfig]}
            series={multiSeriesConfig}
            slots={singleSeries ? { legend: () => null } : undefined}
          />
        )

      case 'line':
        return (
          <LineChart
            height={axisHeight}
            dataset={dataset}
            xAxis={[{ scaleType: 'point', dataKey: 'category', ...categoryAxis }]}
            yAxis={[yAxisConfig]}
            margin={{ right: 40 }}
            series={multiSeriesConfig}
            slots={singleSeries ? { legend: () => null } : undefined}
          />
        )

      case 'pie':
        return (
          <PieChart
            height={pieHeight}
            series={[
              {
                data: data.map((item, i) => ({
                  id: i,
                  label: item.label,
                  value: item.value,
                  color: item.color ?? chartColors[i % chartColors.length],
                })),
                innerRadius: chartDimensions.pie.innerRadius.sm,
                outerRadius: pieOuterRadius,
                paddingAngle: chartDimensions.pie.paddingAngle,
                cornerRadius: chartDimensions.pie.cornerRadius,
                highlightScope: { fade: 'global', highlight: 'item' },
              },
            ]}
          />
        )

      case 'scatter': {
        const xLabel = xAxis?.label ?? 'x'
        const yLabel = yAxis?.label ?? 'y'
        const scatterSeries = series.map((s, i) => {
          const points = isPointSeries(s.data)
            ? s.data.map((p, idx) => ({ x: p.x, y: p.y, id: `${s.label}-${idx}` }))
            : s.data.map((y, idx) => ({ x: idx, y, id: `${s.label}-${idx}` }))
          return {
            label: s.label,
            color: s.color ?? chartColors[i % chartColors.length],
            data: points,
            valueFormatter: (v: { x: number; y: number } | null): string =>
              v ? `${xLabel}: ${v.x}, ${yLabel}: ${v.y}` : '',
          }
        })
        const allPoints = scatterSeries.flatMap((s) => s.data)
        const allIntegerX = allPoints.every((p) => Number.isInteger(p.x))
        return (
          <ScatterChart
            height={axisHeight}
            xAxis={[
              {
                scaleType: 'linear',
                tickMinStep: allIntegerX ? 1 : undefined,
                ...categoryAxis,
              },
            ]}
            yAxis={[
              {
                scaleType: 'linear',
                tickMinStep: integerYTicks ? 1 : undefined,
                ...categoryAxis,
              },
            ]}
            series={scatterSeries}
            slots={singleSeries ? { legend: () => null } : undefined}
          />
        )
      }

      default:
        return null
    }
  }

  const rendered = renderChart()
  if (!rendered) return null

  return (
    <Box className='assistant-chart'>
      <Box ref={exportRef} className='assistant-chart__export-area'>
        <Box className='assistant-chart__axis-row'>
          {yAxis?.label && (
            <Typography variant='caption' className='assistant-chart__y-label'>
              {yAxis.label}
            </Typography>
          )}
          <Box className='assistant-chart__content'>
            {title && (
              <Typography variant='h6' className='assistant-chart__title'>
                {title}
              </Typography>
            )}
            {rendered}
            {xAxis?.label && (
              <Typography variant='caption' className='assistant-chart__x-label'>
                {xAxis.label}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      <Box className='assistant-chart__download-row'>
        <Tooltip title='Download chart as PNG'>
          <IconButton size='small' onClick={handleDownload}>
            <Icon name='DownloadIcon' fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default AssistantChartRenderer
